/**
 * A style's sizes as grids: one per length, colors down, sizes across.
 *
 * A style that comes in lengths — Sugarbush pants are waist 2 to 20, each in
 * Short, Regular and Long — has three sizes for every waist and color. The
 * order grid keyed its cells on color and size alone, so the three lengths
 * landed in one cell and two of them could not be ordered from the grid at
 * all (1,082 such sizes in the catalog). Steve asked for exactly this layout:
 * one grid for short, one for regular, one for long (FB-1078).
 *
 * Shared by the order grid and the style page, so both lay a style out the
 * same way. Every size appears in exactly one cell of exactly one grid.
 */

export interface GridVariant {
  readonly colorName: string | null;
  readonly sizeLabel: string | null;
  /** The second axis — a length, an arch, a durometer. Null on most styles. */
  readonly altSize?: string | null;
  /**
   * The store's order for this size (size aliases, then numbers). When given,
   * the columns follow it rather than the order the cells arrived in — an
   * order's lines arrive as they were typed, and read 6, 4, 10 (2026-10-02).
   */
  readonly sizeSort?: number;
}

export interface GridRow<T> {
  readonly colorName: string | null;
  /**
   * What the row is called: the color, or "One color". A second size with the
   * same color and size — an import made two — gets a row of its own, "Black
   * (2)", rather than sharing a box where only one of them could be edited.
   */
  readonly label: string;
  /** Unique within its grid, for React. */
  readonly key: string;
  /** One entry per size column; undefined where this color has no such size. */
  readonly cells: readonly (T | undefined)[];
}

export interface GridBlock<T> {
  /** The length this grid is for, or null on a style with no second axis. */
  readonly altSize: string | null;
  /**
   * What the grid is called above it: its length, or "No length" for the sizes
   * a style had before it came in lengths (2026-10-02) — an unlabelled fourth
   * grid read as a mistake. Null when the style has no second axis at all.
   */
  readonly label: string | null;
  readonly sizes: readonly (string | null)[];
  readonly rows: readonly GridRow<T>[];
}

/** In the order the variants arrive, which is the catalog's size order. */
function distinct<V>(values: readonly V[]): V[] {
  return [...new Set(values)];
}

/**
 * Whether the second axis is a real second dimension — some color has one
 * size in more than one length — or only another name for the same size, as
 * a kids' "S" that is also an "18". Only a real one needs a grid per length;
 * split by a mere second name, a style would fall apart into one-column grids.
 */
function hasLengths(variants: readonly GridVariant[]): boolean {
  const seen = new Map<string, string | null>();
  for (const variant of variants) {
    const key = `${variant.colorName ?? ''}|${variant.sizeLabel ?? ''}`;
    const length = variant.altSize ?? null;
    if (seen.has(key) && seen.get(key) !== length) return true;
    seen.set(key, length);
  }
  return false;
}

/** Size columns in the store's order when the cells carry it, else as they came. */
function orderedSizes(variants: readonly GridVariant[]): (string | null)[] {
  const sizes = distinct(variants.map((variant) => variant.sizeLabel));
  if (!variants.every((variant) => typeof variant.sizeSort === 'number')) return sizes;
  const rank = new Map<string | null, number>();
  for (const variant of variants) {
    const at = rank.get(variant.sizeLabel);
    if (at === undefined || variant.sizeSort! < at) rank.set(variant.sizeLabel, variant.sizeSort!);
  }
  return [...sizes].sort((a, b) => rank.get(a)! - rank.get(b)!);
}

/**
 * Colors down, sizes across — every variant in a box of its own. Two with the
 * same color and size (2026-10-14: a style imported twice over) each get a
 * row, where before the second was hidden behind the first.
 */
function rowsOf<T extends GridVariant>(
  variants: readonly T[],
  sizes: readonly (string | null)[],
): GridRow<T>[] {
  const colors = distinct(variants.map((variant) => variant.colorName));
  return colors.flatMap((colorName) => {
    const bySize = sizes.map((size) =>
      variants.filter((variant) => variant.colorName === colorName && variant.sizeLabel === size),
    );
    const depth = Math.max(1, ...bySize.map((found) => found.length));
    return Array.from({ length: depth }, (_, occurrence) => ({
      colorName,
      label: `${colorName ?? 'One color'}${occurrence === 0 ? '' : ` (${occurrence + 1})`}`,
      key: `${colorName ?? ''}#${occurrence}`,
      cells: bySize.map((found) => found[occurrence]),
    }));
  });
}

export function sizeGrids<T extends GridVariant>(variants: readonly T[]): GridBlock<T>[] {
  if (!hasLengths(variants)) {
    const sizes = orderedSizes(variants);
    return [{ altSize: null, label: null, sizes, rows: rowsOf(variants, sizes) }];
  }
  // The sizes with no length, made before the style came in lengths, go last.
  const lengths = distinct(variants.map((variant) => variant.altSize ?? null)).sort(
    (a, b) => Number(a === null) - Number(b === null),
  );
  return lengths.map((altSize) => {
    const inBlock = variants.filter((variant) => (variant.altSize ?? null) === altSize);
    const sizes = orderedSizes(inBlock);
    return { altSize, label: altSize ?? 'No length', sizes, rows: rowsOf(inBlock, sizes) };
  });
}
